import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { IconComponent } from '@backstage/core-plugin-api';
import type { ComponentType } from 'react';
import type { SvgIconProps } from '@material-ui/core/SvgIcon';

// IconType matches the type returned by our icon utilities
type IconType = IconComponent | ComponentType<SvgIconProps>;

interface NavItemData {
  title: string;
  to?: string;
  icon?: IconType;
}

interface NavGroupProps {
  title: string;
  icon?: IconType;
  items: NavItemData[];
  priority?: number;
  isUtility?: boolean;
}

export const NavGroup: React.FC<NavGroupProps> = ({
  title,
  icon,
  items,
  isUtility = false,
}) => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout>();
  const isMountedRef = useRef(true);

  const styles = {
    business: {
      base: 'inline-flex items-center px-3 py-2 text-sm font-medium border-b-2 transition-colors duration-200 relative',
      active: 'border-[var(--appvia-primary-light)] text-[var(--appvia-text-primary)]',
      inactive:
        'border-transparent text-[var(--appvia-text-primary)] hover:text-[var(--appvia-text-secondary)] hover:border-[var(--appvia-divider)]',
    },
    utility: {
      base: 'inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200',
      active: 'bg-[var(--appvia-info-light)] text-[var(--appvia-info-main)] border border-[var(--appvia-info-main)]',
      inactive:
        'text-[var(--appvia-text-secondary)] hover:bg-[var(--appvia-background-default)] hover:text-[var(--appvia-text-primary)] border border-transparent',
    },
    dropdown: {
      container:
        'absolute top-full left-0 -mt-1 w-48 bg-[var(--appvia-background-paper)] rounded-md shadow-lg border border-[var(--appvia-divider)] py-1 z-[9999] pt-2',
      item: {
        base: 'flex items-center px-4 py-2 text-sm transition-colors duration-200',
        active: 'bg-[var(--appvia-info-light)] text-[var(--appvia-info-main)]',
        inactive: 'text-[var(--appvia-text-primary)] hover:bg-[var(--appvia-background-default)]',
      },
    },
  };

  useEffect(() => {
    isMountedRef.current = true;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        isMountedRef.current &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      isMountedRef.current = false;
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleMouseEnter = () => {
    // Clear any pending close timeout
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = undefined;
    }
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    // Debounce the close to prevent flicker
    closeTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        setIsOpen(false);
      }
    }, 100);
  };

  const isGroupActive = () => {
    return items.some(item => {
      if (!item.to) return false;
      // Check exact match including query params
      return location.pathname + location.search === item.to;
    });
  };

  const isItemActive = (item: NavItemData) => {
    if (!item.to) return false;
    // Check exact match including query params
    return location.pathname + location.search === item.to;
  };

  const renderIcon = (
    iconComponent: IconType | undefined,
    className: string,
  ) => {
    if (!iconComponent) return null;
    const Icon = iconComponent;
    return (
      <span className={className}><Icon /></span>
    );
  };

  const renderNavItem = (item: NavItemData) => {
    const isActive = isItemActive(item);
    const className = `${styles.dropdown.item.base} ${
      isActive ? styles.dropdown.item.active : styles.dropdown.item.inactive
    }`;

    if (!item.to) {
      return (
        <div key={item.title} className={className}>
          {renderIcon(
            item.icon,
            'mr-2 inline-flex items-center justify-center w-4 h-4',
          )}
          {item.title}
        </div>
      );
    }

    return (
      <Link
        key={item.to}
        to={item.to}
        className={className}
        onClick={() => {
          // Clear timeout to prevent delayed close
          if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
          }
          setIsOpen(false);
        }}
      >
        {renderIcon(
          item.icon,
          'mr-2 inline-flex items-center justify-center w-4 h-4',
        )}
        {item.title}
      </Link>
    );
  };

  const isActive = isGroupActive();
  const styleConfig = isUtility ? styles.utility : styles.business;
  const baseClasses = styleConfig.base;
  const activeClasses = styleConfig.active;
  const inactiveClasses = styleConfig.inactive;

  return (
    <div
      className="relative"
      ref={dropdownRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        className={`${baseClasses} ${
          isActive ? activeClasses : inactiveClasses
        } cursor-pointer bg-transparent border-none h-16`}
      >
        {renderIcon(
          icon,
          'mr-1.5 inline-flex items-center justify-center w-4 h-4',
        )}
        {title}
      </button>

      {isOpen && items.length > 0 && (
        <div className={styles.dropdown.container}>
          {items.map(renderNavItem)}
        </div>
      )}
    </div>
  );
};
